import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { NotificationService } from '../../../notification/notification.service';
import { AuthService } from '../../../auth/auth.service';
import { roleNamesOf } from '../../../auth/menu-access';
import { PurchaseOrderStatus, PurchaseOrderView, PurchasingService } from '../../api';

const STEPS: { status: PurchaseOrderStatus; label: string }[] = [
  { status: 'sent', label: 'Megrendelve' },
  { status: 'confirmed', label: 'Visszaigazolva' },
  { status: 'shipped', label: 'Feladva' },
  { status: 'received', label: 'Megérkezett' },
];
const ORDER: PurchaseOrderStatus[] = ['pending', 'approved', 'sent', 'confirmed', 'shipped', 'received'];

/** Beszerzés → Rendelések: a beszállítói rendelések állapota a jóváhagyástól a megérkezésig. */
@Component({
  selector: 'app-beszerzes-rendelesek',
  templateUrl: './rendelesek.component.html',
  styleUrl: './rendelesek.component.scss',
})
export class BeszerzesRendelesekComponent implements OnInit, OnDestroy {
  filter: 'active' | 'all' | 'received' = 'active';
  loading = true;
  orders: PurchaseOrderView[] = [];
  focusId: number | null = null;
  busy: Record<number, boolean> = {};
  /** levél-szerkesztő (e-mailes rendelés kézi küldése) */
  mailEdit: { id: number; subject: string; body: string } | null = null;
  /** webshopos leadás: rendelésszám */
  refEdit: Record<number, string> = {};
  readonly steps = STEPS;
  private readonly subs: Subscription[] = [];

  constructor(private purchasing: PurchasingService, private route: ActivatedRoute, private notification: NotificationService, private auth: AuthService) {}

  ngOnInit(): void {
    this.subs.push(
      this.route.queryParamMap.subscribe((params) => {
        const id = Number(params.get('po'));
        this.focusId = Number.isInteger(id) && id > 0 ? id : null;
        if (this.focusId) this.filter = 'all';
        this.load();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  get isAdmin(): boolean {
    return roleNamesOf(this.auth.user).includes('admin');
  }

  setFilter(filter: 'active' | 'all' | 'received'): void {
    this.filter = filter;
    this.load();
  }

  load(): void {
    this.loading = !this.orders.length;
    const query = this.filter === 'active' ? { active: true } : this.filter === 'received' ? { status: 'received' } : {};
    this.purchasing.listOrders(query).subscribe({
      next: (orders) => {
        this.orders = orders;
        this.loading = false;
        if (this.focusId) setTimeout(() => document.getElementById(`po-${this.focusId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
      },
      error: () => (this.loading = false),
    });
  }

  stepDone(order: PurchaseOrderView, step: PurchaseOrderStatus): boolean {
    return order.status !== 'cancelled' && ORDER.indexOf(order.status) >= ORDER.indexOf(step);
  }

  private replace(updated: PurchaseOrderView): void {
    this.orders = this.orders.map((o) => (o.id === updated.id ? updated : o));
  }

  private act(order: PurchaseOrderView, call: () => any, done: string): void {
    this.busy[order.id] = true;
    call().subscribe({
      next: (updated: PurchaseOrderView) => {
        this.busy[order.id] = false;
        this.replace(updated);
        this.notification.show(done, 'success');
      },
      error: (e: any) => {
        this.busy[order.id] = false;
        this.notification.show(e?.error?.message || 'Nem sikerült.', 'danger');
      },
    });
  }

  approve(order: PurchaseOrderView): void {
    this.act(order, () => this.purchasing.approve(order.id), `${order.displayId} jóváhagyva — Foxi intézi.`);
  }

  openMail(order: PurchaseOrderView): void {
    this.purchasing.getOrder(order.id).subscribe((full) => {
      this.mailEdit = { id: order.id, subject: full.defaultEmail?.subject || '', body: full.defaultEmail?.body || '' };
    });
  }

  sendMail(order: PurchaseOrderView): void {
    const edit = this.mailEdit;
    if (!edit || !edit.body.trim()) return;
    this.act(order, () => this.purchasing.send(order.id, edit.subject, edit.body), `${order.displayId} elküldve a beszállítónak.`);
    this.mailEdit = null;
  }

  markOrdered(order: PurchaseOrderView): void {
    this.act(order, () => this.purchasing.markOrdered(order.id, this.refEdit[order.id] || ''), `${order.displayId} megrendelve.`);
  }

  setStatus(order: PurchaseOrderView, status: 'confirmed' | 'shipped'): void {
    this.act(order, () => this.purchasing.update(order.id, { status }), 'Állapot frissítve.');
  }

  receive(order: PurchaseOrderView): void {
    if (!confirm(`Megjött a(z) ${order.supplier?.name} csomag (${order.displayId})? A tételek bevételeződnek.`)) return;
    this.act(order, () => this.purchasing.receive(order.id), `${order.displayId} megérkezett, bevételezve. 📦`);
  }

  // ------------------------------------------------------------ webshop-robot

  /** képernyőképek (blob-URL), robot-futásonként */
  shots: Record<number, string> = {};

  robotLabel(order: PurchaseOrderView): string {
    const run = order.robotRun;
    if (!run) return '';
    const total = run.totalHuf ? ` · ${Math.round(run.totalHuf).toLocaleString('hu-HU')} Ft` : '';
    switch (run.status) {
      case 'running': return '🤖 A robot most dolgozik ezen a rendelésen…';
      case 'ready': return `🤖 Próba kész: a robot az összegző oldalon megállt${total} — jóváhagyásra vár`;
      case 'ordered': return `🤖 A robot leadta${run.orderRef ? ' · rendelésszám: ' + run.orderRef : ''}${total}`;
      case 'blocked': return `🤖 A robot elakadt: ${run.note || ''}`;
      default: return `🤖 A robot nem járt sikerrel: ${run.note || ''}`;
    }
  }

  showShot(order: PurchaseOrderView): void {
    const run = order.robotRun;
    if (!run?.hasScreenshot || this.shots[run.id]) return;
    this.purchasing.robotScreenshot(run.id).subscribe({
      next: (blob) => (this.shots[run.id] = URL.createObjectURL(blob)),
      error: () => this.notification.show('A képernyőkép nem érhető el.', 'warning'),
    });
  }

  startRobot(order: PurchaseOrderView): void {
    this.act(order, () => this.purchasing.robot(order.id), 'A robot elindult — pár perc, az eredményről Foxi szól a chatben. 🤖');
    setTimeout(() => this.load(), 4000);
  }

  robotGo(order: PurchaseOrderView): void {
    if (!confirm(`A robot leadja a rendelést a(z) ${order.supplier?.name} webshopban (${order.robotRun?.totalHuf ? Math.round(order.robotRun.totalHuf) + ' Ft, ' : ''}utánvét). Mehet?`)) return;
    this.act(order, () => this.purchasing.robotGo(order.id), 'Rendben — a robot most leadja. 🛒');
    setTimeout(() => this.load(), 4000);
  }

  /** Törölhető: ami még nem ment el (bárki), illetve a lemondott (admin). */
  canDelete(order: PurchaseOrderView): boolean {
    return order.status === 'pending' || order.status === 'approved' || (order.status === 'cancelled' && this.isAdmin);
  }

  remove(order: PurchaseOrderView): void {
    if (!confirm(`Biztosan törlöd: ${order.displayId} (${order.supplier?.name})? Ez nem vonható vissza.`)) return;
    this.busy[order.id] = true;
    this.purchasing.deleteOrder(order.id).subscribe({
      next: () => {
        this.busy[order.id] = false;
        this.orders = this.orders.filter((o) => o.id !== order.id);
        this.notification.show(`${order.displayId} törölve.`, 'success');
      },
      error: (e) => {
        this.busy[order.id] = false;
        this.notification.show(e?.error?.message || 'Nem sikerült törölni.', 'danger');
      },
    });
  }

  cancel(order: PurchaseOrderView): void {
    if (!confirm(`Biztosan lemondod: ${order.displayId} (${order.supplier?.name})?`)) return;
    this.act(order, () => this.purchasing.cancel(order.id), `${order.displayId} lemondva.`);
  }
}
